import React, { useCallback, useEffect, useRef, useState } from 'react';
import SvgPieChart from '../reports/SvgPieChart';
import FleetScoreTrendPanel from '../reports/FleetScoreTrendPanel';
import FleetNarrativePanel from '../reports/FleetNarrativePanel';
import ProblematicDevicesTable from '../reports/ProblematicDevicesTable';
import DeviceSnapshotModal from '../reports/DeviceSnapshotModal';
import SectionTooltip from '../reports/SectionTooltip';
import { fetchFleetSummary, fetchFleetHealth, fetchFleetHealthTrend, fetchFleetHealthCounts, fetchFleetWidespreadIssues, fetchProblematicDevices } from '../api/restapi';
import '../assets/FleetAnalytics.css';
import '../assets/Reports.css';
import '../assets/NewDiagnostics.css';

const DURATIONS = [
    { key: '1d',  label: 'D',   days: 1,  summaryKey: '1d',  scoreTitle: 'Past Day'  },
    { key: '7d',  label: 'W',  days: 7,  summaryKey: '7d',  scoreTitle: 'Past Week' },
    { key: '1mo', label: 'M', days: 30, summaryKey: '30d', scoreTitle: 'Past Month' },
];

const SUBDOMAIN_LABELS = {
    DATA_STALL: 'Data stalls',
    SIGNAL: 'Signal issues',
    REGISTRATION: 'Registration issues',
    PDN: 'PDN connectivity issues',
    THROUGHPUT: 'Throughput degradation',
    CPU: 'High CPU usage',
    MEMORY: 'Memory pressure',
    FLASH: 'Flash storage issues',
    THERMAL: 'Thermal issues',
    STABILITY: 'Crash stability issues',
    SSR: 'Modem SSR (crashes)',
    BOOT: 'Boot issues',
};

function issuePriority(issue) {
    return (issue.priority || '').toUpperCase() === 'HIGH' ? 'high' : 'low';
}

function orderedIssues(issues) {
    return [...(issues || [])].sort((a, b) => {
        if (a.severity !== b.severity) return a.severity === 'CRITICAL' ? -1 : 1;
        return (b.affectedDeviceCount || 0) - (a.affectedDeviceCount || 0);
    });
}

function groupIssuesByPriority(issues) {
    return orderedIssues(issues).reduce((groups, issue) => {
        groups[issuePriority(issue)].push(issue);
        return groups;
    }, { high: [], low: [] });
}

const ISSUE_PRIORITY_GROUPS = [
    {
        key: 'high',
        label: 'High Priority',
    },
    {
        key: 'low',
        label: 'Low Priority',
    },
];

function issueBannerText(issue) {
    const label = SUBDOMAIN_LABELS[issue.subdomain] || issue.subdomain;
    return `${label} affecting ${issue.affectedDeviceCount} device${issue.affectedDeviceCount === 1 ? '' : 's'}.`;
}

function formatUptimeParts(seconds) {
    if (!seconds || seconds < 60) return { value: '< 1', unit: 'min' };
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(seconds / 3600);
    const days = Math.floor(seconds / 86400);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    if (seconds < 3600) return { value: String(mins), unit: 'min' };
    if (seconds < 86400) {
        const remMins = mins % 60;
        return { value: String(hrs), unit: `hour${hrs !== 1 ? 's' : ''}${remMins > 0 ? ` ${remMins} min` : ''}` };
    }
    if (days < 30) {
        const remHrs = hrs % 24;
        return { value: String(days), unit: `day${days !== 1 ? 's' : ''}${remHrs > 0 ? ` ${remHrs} hr` : ''}` };
    }
    if (days < 365) {
        const remDays = days % 30;
        return { value: String(months), unit: `month${months !== 1 ? 's' : ''}${remDays > 0 ? ` ${remDays} day${remDays !== 1 ? 's' : ''}` : ''}` };
    }
    const remMonths = months % 12;
    return { value: String(years), unit: `year${years !== 1 ? 's' : ''}${remMonths > 0 ? ` ${remMonths} month${remMonths !== 1 ? 's' : ''}` : ''}` };
}

function formatDateOnly(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function ScoreCardTooltip() {
    return (
        <div className="snapshot-score-tooltip">
            <button
                type="button"
                className="snapshot-score-tooltip-trigger"
                aria-label="Explain health score, status, and trend"
                aria-describedby="fleet-score-tooltip"
            >
                i
            </button>
            <div id="fleet-score-tooltip" className="snapshot-score-tooltip-content" role="tooltip">
                <p><strong>Health score:</strong> the averaged fleet health for the selected lookback period, from 0 to 100. Higher is healthier.</p>
                <p><strong>Calculation:</strong> each device score combines its available health-area scores with configured weights. Health-area scores reflect metric penalty rules; missing telemetry does not count against the score. The fleet score then averages participating devices.</p>
                <p><strong>Status:</strong> STABLE is 75–100, DEGRADED is 50–74, and CRITICAL is below 50.</p>
                <p><strong>Trend:</strong> INCREASING means the score is rising, DECLINING means it is falling, and STABLE means there is no meaningful change.</p>
            </div>
        </div>
    );
}

function FleetProblematicAreasTooltip({ days }) {
    return (
        <SectionTooltip
            id="fleet-problematic-areas-tooltip"
            label="Explain Fleet-Wide Problematic Areas"
            className="fleet-areas-tooltip"
        >
            <p><strong>Fleet-wide scope:</strong> these are health areas whose average score across active devices is DEGRADED or CRITICAL during the selected {days}-day lookback.</p>
            <p>They highlight systemic issues. A Device Snapshot can show additional device-specific scoring-window events that do not affect the fleet-wide average.</p>
        </SectionTooltip>
    );
}

function FleetHealthOverview({ userInfo, role, onNavigateToSubTab, onNavigateToDeviceInvestigation }) {
    const [selectedDuration, setSelectedDuration] = useState('1d');
    const [summary, setSummary] = useState({ data: null, loading: true, error: null });
    const [health, setHealth] = useState({ data: null, loading: true, error: null });
    const [trend, setTrend] = useState({ points: [], loading: true, error: null });
    const [counts, setCounts] = useState({ data: null, loading: true, error: null });
    const [widespreadIssues, setWidespreadIssues] = useState({ data: null, loading: true, error: null });
    const [problematicDevices, setProblematicDevices] = useState({ data: null, loading: true, error: null });
    const [snapshotDevice, setSnapshotDevice] = useState(null);

    // Fleet snapshot AI assessment
    const [snapshot, setSnapshot] = useState({ data: null, loading: true, error: null, warming: null });
    const apigeeTokenRef = useRef(null);
    const MAX_SNAPSHOT_RETRIES = 3;

    const ensureApigeeToken = useCallback(async () => {
        if (apigeeTokenRef.current) return apigeeTokenRef.current;
        try {
            const res = await fetch((process.env.REACT_APP_WBC_API_BASE_URL || '') + '/api/v1/apigee/token');
            if (res.ok) {
                const d = await res.json();
                apigeeTokenRef.current = d.access_token || null;
            }
        } catch (e) { /* silent */ }
        return apigeeTokenRef.current;
    }, []);

    const fetchSnapshot = useCallback(async (days, attempt = 0) => {
        const base = process.env.REACT_APP_WBC_AI_INSIGHTS_URL;
        const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2';
        const url = `${base}/api/${ver}/insights/fleet/snapshot-assessment?days=${days}`;

        setSnapshot(prev => ({ ...prev, loading: true, error: null }));

        try {
            const token = await ensureApigeeToken();
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await fetch(url, { headers });

            if (res.status === 200) {
                const data = await res.json();
                setSnapshot({ data, loading: false, error: null, warming: null });
            } else if (res.status === 202) {
                const body = await res.json();
                if (attempt < MAX_SNAPSHOT_RETRIES) {
                    const retryAfter = (body.retry_after || 30) * 1000;
                    setSnapshot(prev => ({
                        ...prev,
                        loading: true,
                        error: null,
                        warming: `Generating insight… (attempt ${attempt + 1}/${MAX_SNAPSHOT_RETRIES})`,
                    }));
                    setTimeout(() => fetchSnapshot(days, attempt + 1), retryAfter);
                } else {
                    setSnapshot({ data: null, loading: false, error: 'Insight generation timed out.', warming: null });
                }
            } else if (res.status === 503) {
                setSnapshot({ data: null, loading: false, error: 'AI Insights service is currently unavailable.', warming: null });
            } else if (res.status === 502) {
                setSnapshot({ data: null, loading: false, error: 'Insight generation failed. Try again later.', warming: null });
            } else if (res.status === 400) {
                setSnapshot({ data: null, loading: false, error: 'Invalid request parameters.', warming: null });
            } else {
                setSnapshot({ data: null, loading: false, error: null, warming: null });
            }
        } catch (e) {
            setSnapshot({ data: null, loading: false, error: null, warming: null });
        }
    }, [ensureApigeeToken]);

    const loadAll = useCallback(async (durKey) => {
        const dur = DURATIONS.find(d => d.key === durKey) || DURATIONS[1];

        setSummary(prev => ({ ...prev, loading: true, error: null }));
        setHealth(prev => ({ ...prev, loading: true, error: null }));
        setTrend(prev => ({ ...prev, loading: true, error: null }));
        setCounts(prev => ({ ...prev, loading: true, error: null }));
        setWidespreadIssues(prev => ({ ...prev, loading: true, error: null }));
        setProblematicDevices(prev => ({ ...prev, loading: true, error: null }));

        const [summaryRes, healthRes, trendRes, countsRes, issuesRes, devicesRes] = await Promise.all([
            fetchFleetSummary(dur.summaryKey),
            fetchFleetHealth(dur.days),
            fetchFleetHealthTrend(dur.days),
            fetchFleetHealthCounts(dur.days),
            fetchFleetWidespreadIssues(dur.days),
            fetchProblematicDevices(dur.days),
        ]);

        setSummary(summaryRes?.errorMessage
            ? { data: null, loading: false, error: summaryRes.errorMessage }
            : { data: summaryRes?.data?.fleet_stats || summaryRes?.fleet_stats || null, loading: false, error: null }
        );

        setHealth(healthRes?.errorMessage
            ? { data: null, loading: false, error: healthRes.errorMessage }
            : { data: healthRes, loading: false, error: null }
        );

        const trendPayload = trendRes?.data || trendRes;
        const rawTrend = trendPayload?.trend;
        const windowHours = trendPayload?.window_hours;
        if (Array.isArray(rawTrend) && windowHours) {
            setTrend({
                data: {
                    trend: rawTrend,
                    subdomain_trends: Array.isArray(trendPayload?.subdomain_trends)
                        ? trendPayload.subdomain_trends
                        : [],
                },
                windowHours,
                loading: false,
                error: null,
            });
        } else {
            setTrend({ data: null, windowHours: null, loading: false, error: trendRes?.errorMessage || null });
        }

        setCounts(countsRes?.errorMessage
            ? { data: null, loading: false, error: countsRes.errorMessage }
            : { data: countsRes, loading: false, error: null }
        );

        setWidespreadIssues(issuesRes?.errorMessage
            ? { data: null, loading: false, error: issuesRes.errorMessage }
            : { data: issuesRes, loading: false, error: null }
        );

        setProblematicDevices(devicesRes?.errorMessage
            ? { data: null, loading: false, error: devicesRes.errorMessage }
            : { data: devicesRes, loading: false, error: null }
        );

        // Kick off snapshot assessment independently (has its own polling loop)
        fetchSnapshot(dur.days, 0);
    }, [fetchSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadAll(selectedDuration);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDurationChange = (key) => {
        if (key === selectedDuration) return;
        setSelectedDuration(key);
        loadAll(key);
    };

    const durLabel = DURATIONS.find(d => d.key === selectedDuration)?.label ?? selectedDuration;

    const totalDevices = summary.data?.total_devices ?? 0;
    const avgUptimeSeconds = summary.data?.avg_uptime_seconds ?? 0;
    const avgUptimeHours = Math.round((avgUptimeSeconds / 3600) * 10) / 10;
    const avgUptimeModemSeconds = summary.data?.avg_uptime_modem_seconds ?? 0;
    const avgUptimeApSeconds = summary.data?.avg_uptime_ap_seconds ?? 0;
    const telemetryActive = summary.data?.devices_with_mbb_uploads ?? 0;
    const noTelemetry = Math.max(totalDevices - telemetryActive, 0);
    const telemetryCoveragePct = totalDevices > 0 ? Math.round((telemetryActive / totalDevices) * 100) : 0;
    const activeDiagnostics = summary.data?.active_diagnostics_count ?? 0;
    const devicesOnboarded = summary.data?.devices_onboarded ?? 0;
    const devicesCrowdsourcing = summary.data?.devices_uploading_crowdsourcing_data ?? 0;
    const devicesDiagnosticsRegistered = summary.data?.devices_registered_diagnostics ?? 0;
    const totalDevicesAllSources = summary.data?.total_devices_all_sources ?? 0;

    const fleetScore = health.data?.fleetScorePct ?? null;
    const stableCount = counts.data?.stableCount ?? 0;
    const degradedCount = counts.data?.degradedCount ?? 0;
    const criticalCount = counts.data?.criticalCount ?? 0;
    const issueGroups = groupIssuesByPriority(widespreadIssues.data?.issues);
    const scoreTitle = DURATIONS.find(d => d.key === selectedDuration)?.scoreTitle || 'Averaged Score';
    const displayedScore = snapshot.data?.context?.fleetScorePct ?? fleetScore;
    const avgUptime = formatUptimeParts(avgUptimeSeconds);

    return (
        <div className="fho-dashboard">

            <div className="duration-bar fh-duration-control" aria-label="Historical Health duration">
                <span className="duration-label">Duration:</span>
                {DURATIONS.map(d => (
                    <button
                        type="button"
                        key={d.key}
                        className={`filter-chip${selectedDuration === d.key ? ' active' : ''}`}
                        aria-pressed={selectedDuration === d.key}
                        onClick={() => handleDurationChange(d.key)}
                    >
                        {d.label}
                    </button>
                ))}
            </div>

            {/* ── Overall Health — operational summary ── */}
            <section aria-labelledby="overall-health-heading">
                <div className="sec-hdr fh-section-header-first">
                    <div className="sec-icon blue">📡</div>
                    <h2 id="overall-health-heading">Overall Health</h2>
                    <SectionTooltip id="overall-health-tooltip" label="Explain Overall Health">
                        <p><strong>Health Distribution:</strong> each active device’s latest raw health score in the selected D/W/M lookback: STABLE ≥75, DEGRADED 50–74, CRITICAL &lt;50.</p>
                        <p><strong>Devices Onboarded:</strong> an as-of count at the summary endpoint’s cutoff, not devices newly onboarded during D/W/M.</p>
                        <p><strong>Uploading Telemetry Data</strong> and <strong>Registered for Diagnostics:</strong> distinct-device counts during D/W/M.</p>
                        <p><strong>Avg Uptime:</strong> the latest reported device uptime averaged across onboarded devices; it is not duration-filtered.</p>
                    </SectionTooltip>
                    <div className="line" />
                </div>
                <div className="fh-overall-health-grid" style={{ marginBottom: 14 }}>
                    <div className="fh-overall-health-distribution">
                        <SvgPieChart
                            title="Health Distribution"
                            labels={['Stable', 'Degraded', 'Critical']}
                            values={[stableCount, degradedCount, criticalCount]}
                            colors={['#059669', '#d97706', '#dc2626']}
                            size={170}
                        />
                    </div>
                    <div className="kpi-card kpi-purple fh-overall-health-card fh-overall-health-card--onboarded">
                        <div className="kpi-label">Devices Onboarded</div>
                        <div className="fh-overall-health-value">{devicesOnboarded.toLocaleString()}</div>
                    </div>
                    <div className="kpi-card kpi-green fh-overall-health-card fh-overall-health-card--telemetry">
                        <div className="kpi-label">Uploading Telemetry Data</div>
                        <div className="fh-overall-health-value">{devicesCrowdsourcing.toLocaleString()}</div>
                    </div>
                    <div className="kpi-card fh-overall-health-card fh-overall-health-card--registered">
                        <div className="kpi-label">Registered for Diagnostics</div>
                        <div className="fh-overall-health-value">{devicesDiagnosticsRegistered.toLocaleString()}</div>
                    </div>
                    <div className="kpi-card kpi-amber fh-overall-health-card fh-overall-health-card--uptime">
                        <div className="kpi-label">Avg Uptime</div>
                        <div className="fh-overall-health-value">{avgUptime.value}</div>
                        <div className="fh-overall-health-descriptor">{avgUptime.unit}</div>
                    </div>
                    <div className="kpi-card kpi-amber fh-overall-health-card fh-overall-health-card--uptime-modem">
                        <div className="kpi-label">Avg Modem Uptime</div>
                        <div className="fh-overall-health-value">{formatUptimeParts(avgUptimeModemSeconds).value}</div>
                        <div className="fh-overall-health-descriptor">{formatUptimeParts(avgUptimeModemSeconds).unit}</div>
                    </div>
                    <div className="kpi-card kpi-amber fh-overall-health-card fh-overall-health-card--uptime-ap">
                        <div className="kpi-label">Avg AP Uptime</div>
                        <div className="fh-overall-health-value">{formatUptimeParts(avgUptimeApSeconds).value}</div>
                        <div className="fh-overall-health-descriptor">{formatUptimeParts(avgUptimeApSeconds).unit}</div>
                    </div>
                </div>
                {(health.error || counts.error || summary.error) && (
                    <p className="fh-no-data">{health.error || counts.error || summary.error}</p>
                )}
            </section>

            {/* ── Historical Health — duration-dependent analysis ── */}
            <section aria-labelledby="historical-health-heading">
                <div className="sec-hdr fh-historical-health-header">
                    <div className="sec-icon purple">📈</div>
                    <h2 id="historical-health-heading">Historical Health</h2>
                    <SectionTooltip id="historical-health-tooltip" label="Explain Historical Health">
                        <p>D/W/M controls the fleet score’s raw-window average, trend history, AI narrative, health distribution, problematic areas, and Problematic Devices.</p>
                        <p>Overall Health’s Devices Onboarded and Avg Uptime cards are exceptions, as described in the Overall Health help.</p>
                    </SectionTooltip>
                    <div className="line" />
                </div>
                <div className="snapshot-row">
                    {/* Left: score KPI */}
                    <div className="snapshot-score-card kpi-card">
                    <div className="snapshot-score-header">
                        <div className="snapshot-score-heading">
                            <div className="kpi-label">{scoreTitle}</div>
                            <div className="snapshot-score-descriptor">Average health score</div>
                        </div>
                        <ScoreCardTooltip />
                    </div>
                    <div className="kpi-value snapshot-score-value">
                        {displayedScore != null ? (
                            <>
                                <span className="snapshot-score-number">{displayedScore}</span>
                                <span className="snapshot-score-denominator">/100</span>
                            </>
                        ) : '—'}
                    </div>
                    <div className="snapshot-score-footer">
                        {snapshot.data?.context && (() => {
                            const status = snapshot.data.context.fleetStatus || '';
                            const trend = snapshot.data.context.trend || '';
                            const displayTrend = trend === 'IMPROVING' ? 'INCREASING' : trend;
                            const delta = snapshot.data.context.trendDelta ?? 0;
                            const statusClass = status === 'STABLE' ? 'snapshot-status-healthy'
                                : status === 'DEGRADED' ? 'snapshot-status-atrisk'
                                : status === 'CRITICAL' ? 'snapshot-status-critical'
                                : 'snapshot-status-healthy';
                            const trendArrow = trend === 'IMPROVING' ? '↑' : trend === 'DECLINING' ? '↓' : '→';
                            const trendClass = trend === 'IMPROVING' ? 'snapshot-trend-up'
                                : trend === 'DECLINING' ? 'snapshot-trend-down'
                                : 'snapshot-trend-stable';
                            const deltaPct = delta !== 0 ? ` (${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}%)` : '';
                            return (
                                <div className="snapshot-score-status-row">
                                    <div className={`snapshot-status-line ${statusClass}`}>● {status}</div>
                                    <div className={`snapshot-trend-line ${trendClass}`}>{trendArrow} {displayTrend}{deltaPct}</div>
                                </div>
                            );
                        })()}
                        {(health.data?.from || health.data?.to) && (
                            <div className="snapshot-updated snapshot-score-period">
                                {formatDateOnly(health.data.from)} – {formatDateOnly(health.data.to)}
                            </div>
                        )}
                    </div>
                    </div>

                    <div className="snapshot-trend-card">
                        <FleetScoreTrendPanel trend={trend} selectedDuration={selectedDuration} compact />
                    </div>
                </div>

                {/* ── AI narrative ── */}
                <div className="snapshot-narrative-row">
                    <FleetNarrativePanel
                        snapshot={snapshot}
                        selectedDuration={selectedDuration}
                        onRetry={() => fetchSnapshot(DURATIONS.find(d => d.key === selectedDuration)?.days ?? 7, 0)}
                    />
                </div>
            </section>

            {/* ── Problematic Areas ── */}
            <div className="sec-hdr">
                <div className="sec-icon orange">⚠️</div>
                <h2>Fleet-Wide Problematic Areas</h2>
                <FleetProblematicAreasTooltip days={DURATIONS.find((duration) => duration.key === selectedDuration)?.days ?? 7} />
                <div className="line" />
            </div>
            {widespreadIssues.loading && <p className="fh-loading">Loading…</p>}
            {!widespreadIssues.loading && widespreadIssues.error && (
                <p className="fh-error">{widespreadIssues.error}</p>
            )}
            {!widespreadIssues.loading && !widespreadIssues.error && (
                (widespreadIssues.data?.issues?.length ?? 0) === 0 ? (
                    <p className="fh-no-data">No problematic areas detected for the selected lookback period.</p>
                ) : (
                    <div className={`fh-priority-bands${issueGroups.high.length && issueGroups.low.length ? ' fh-priority-bands--two-column' : ''}`}>
                        {ISSUE_PRIORITY_GROUPS.map(group => {
                            const issues = issueGroups[group.key];
                            if (!issues.length) return null;

                            return (
                                <section key={group.key} className="fh-priority-band">
                                    <div className="fh-priority-band-header">
                                        <h3>{group.label}</h3>
                                        <span className="fh-priority-band-count">
                                            {issues.length} {issues.length === 1 ? 'area' : 'areas'}
                                        </span>
                                    </div>
                                    <div className="fh-banner-list">
                                        {issues.map(issue => (
                                            <div
                                                key={issue.subdomain}
                                                className="fh-banner"
                                                onClick={() => onNavigateToSubTab && onNavigateToSubTab('InsightsPatterns', { duration: selectedDuration, subdomain: issue.subdomain })}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => { if (e.key === 'Enter') onNavigateToSubTab && onNavigateToSubTab('InsightsPatterns', { duration: selectedDuration, subdomain: issue.subdomain }); }}
                                            >
                                                <span className="fh-banner-text">{issueBannerText(issue)}</span>
                                                <span className="fh-banner-cta">View →</span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )
            )}

            <div className="sec-hdr">
                <div className="sec-icon red">🧭</div>
                <h2>Problematic Devices</h2>
                <SectionTooltip id="problematic-devices-tooltip" label="Explain Problematic Devices">
                    <p>Shows the latest raw device score for devices active in the selected lookback. Only DEGRADED and CRITICAL devices are shown; D/W/M determines active-device eligibility.</p>
                </SectionTooltip>
                <div className="line" />
            </div>
            {problematicDevices.loading && <p className="fh-loading">Loading…</p>}
            {!problematicDevices.loading && problematicDevices.error && <p className="fh-error">{problematicDevices.error}</p>}
            {!problematicDevices.loading && !problematicDevices.error && (
                (problematicDevices.data?.devices?.length ?? 0) === 0
                    ? <p className="fh-no-data">No degraded or critical devices detected.</p>
                    : <ProblematicDevicesTable devices={problematicDevices.data.devices} onDeviceSelect={setSnapshotDevice} />
            )}

            {snapshotDevice && (
                <DeviceSnapshotModal
                    device={snapshotDevice}
                    days={DURATIONS.find((duration) => duration.key === selectedDuration)?.days ?? 7}
                    onClose={() => setSnapshotDevice(null)}
                    onInvestigate={(anchor) => {
                        setSnapshotDevice(null);
                        onNavigateToDeviceInvestigation?.(anchor);
                    }}
                />
            )}

            {/* ── Continue the Investigation CTA ── */}
            <div className="sec-hdr">
                <div className="sec-icon green">➡️</div>
                <h2>Continue the Investigation</h2>
                <SectionTooltip id="continue-investigation-tooltip" label="Explain Continue the Investigation">
                    <p>This action navigates to Insights &amp; Patterns with the current D/W/M duration preselected.</p>
                </SectionTooltip>
                <div className="line" />
            </div>
            <div className="journey-cta-card">
                <div>
                    <div className="journey-cta-title">Ready to see what's driving these numbers?</div>
                    <div className="journey-cta-desc">Move to Layer 2 — AI-generated insights, anomaly clusters, and recurring failure patterns across the fleet.</div>
                </div>
                <button
                    className="journey-cta-btn"
                    onClick={() => onNavigateToSubTab && onNavigateToSubTab('InsightsPatterns', { duration: selectedDuration })}
                >
                    Go to Insights &amp; Patterns →
                </button>
            </div>
        </div>
    );
}

export default FleetHealthOverview;
